import { z } from 'zod';
import { allUserRoleSchema } from '../data.types.js';
import { BaseResponseSchema, dateSchema, emailSchema, firstNameSchema, integerIdSchema, lastNameSchema, passwordSchema, uuidSchema, } from './primitives.js';
export const CreateTenantRequestSchema = z.object({
    name: z.string().min(1).max(100).trim(),
    subdomain: z.string().min(1).max(50).trim(),
    subscriptionPlanId: uuidSchema.optional(),
    adminEmail: emailSchema,
    adminFirstName: firstNameSchema,
    adminLastName: lastNameSchema,
    customerManagedEncryption: z.boolean().default(false),
    customerKmsKeyArn: z.string().regex(/^arn:aws:kms:[a-z0-9-]+:\d{12}:key\/[a-f0-9-]+$/).optional(),
    customerKmsKeyAlias: z.string().max(256).optional(),
    customerExternalId: z.string().max(128).optional(),
}).refine((data) => {
    if (data.customerManagedEncryption) {
        return !!data.customerKmsKeyArn && !!data.customerExternalId;
    }
    return true;
}, {
    message: 'Customer KMS Key ARN and External ID are required when customer-managed encryption (BYOK) is enabled',
    path: ['customerKmsKeyArn'],
});
export const UpdateTenantRequestSchema = z.object({
    name: z.string().min(1).max(100).trim().optional(),
    subdomain: z.string().min(1).max(50).trim().optional(),
    subscriptionPlanId: uuidSchema.optional(),
    isActive: z.boolean().optional(),
});
export const CreateTenantUserForProviderSchema = z.object({
    email: emailSchema,
    firstName: firstNameSchema,
    lastName: lastNameSchema,
    role: allUserRoleSchema,
    password: passwordSchema.optional(),
});
export const TenantsResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        tenants: z.array(z.object({
            id: uuidSchema,
            name: z.string(),
            subdomain: z.string(),
            isActive: z.boolean(),
            createdAt: dateSchema,
            updatedAt: dateSchema,
            userCount: z.number().int(),
            containerCount: z.number().int(),
            waferCount: z.number().int(),
            storageUsedGB: z.coerce.number(),
            subscriptionPlan: z.string().optional(),
            subscriptionStatus: z.string().optional(),
        })),
        total: z.number().int(),
        page: z.number().int(),
        limit: z.number().int(),
        totalPages: z.number().int(),
    }),
});
export const TenantResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        tenant: z.object({
            id: uuidSchema,
            name: z.string(),
            subdomain: z.string(),
            isActive: z.boolean(),
            createdAt: dateSchema,
            updatedAt: dateSchema,
            userCount: z.number().int(),
            containerCount: z.number().int(),
            waferCount: z.number().int(),
            storageUsedGB: z.coerce.number(),
            subscriptionPlan: z.string().optional(),
            subscriptionStatus: z.string().optional(),
            adminUser: z.object({
                id: integerIdSchema,
                firstName: z.string(),
                lastName: z.string(),
                email: emailSchema,
            }).optional(),
        }),
    }),
});
export const CreateTenantResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        tenant: z.object({
            id: uuidSchema,
            name: z.string(),
            subdomain: z.string(),
            isActive: z.boolean(),
            createdAt: dateSchema,
            adminUser: z.object({
                id: integerIdSchema,
                firstName: z.string(),
                lastName: z.string(),
                email: emailSchema,
                temporaryPassword: z.string().optional(),
            }),
        }),
    }),
});
export const TenantUsersListResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        users: z.array(z.object({
            id: integerIdSchema,
            email: emailSchema,
            firstName: firstNameSchema,
            lastName: lastNameSchema,
            role: z.string(),
            isActive: z.boolean(),
            lastLoginAt: dateSchema.optional(),
            createdAt: dateSchema,
        })),
    }),
});
export const CreateTenantUserResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        user: z.object({
            id: integerIdSchema,
            email: emailSchema,
            firstName: firstNameSchema,
            lastName: lastNameSchema,
            role: z.string(),
            isActive: z.boolean(),
            temporaryPassword: z.string(),
        }),
    }),
});
export const ResendTenantInvitationResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        tenant: z.object({
            id: uuidSchema,
            name: z.string(),
        }),
        adminUser: z.object({
            id: integerIdSchema,
            email: emailSchema,
            firstName: firstNameSchema,
            lastName: lastNameSchema,
        }),
        invitationResent: z.boolean(),
    }),
});
export const TenantDeleteResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        id: uuidSchema,
        name: z.string(),
        deletedPermanently: z.boolean(),
    }),
});
export const TenantUpdateResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        id: uuidSchema,
        name: z.string(),
        subdomain: z.string(),
        isActive: z.boolean(),
        subscriptionPlanId: integerIdSchema.optional(),
        createdAt: dateSchema,
        updatedAt: dateSchema,
    }),
});
export const ProviderStatsResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        totalTenants: z.number().int().optional(),
        activeTenants: z.number().int().optional(),
        totalUsers: z.number().int().optional(),
        totalContainers: z.number().int().optional(),
        totalWafers: z.number().int().optional(),
        totalStorageUsedGB: z.coerce.number().optional(),
        revenue: z.object({
            monthly: z.coerce.number(),
            yearly: z.coerce.number(),
            total: z.coerce.number(),
        }).optional(),
    }),
});
export const TenantUsersResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        users: z.array(z.object({
            id: integerIdSchema,
            email: emailSchema,
            firstName: firstNameSchema,
            lastName: lastNameSchema,
            role: z.string(),
            isActive: z.boolean(),
            emailVerified: z.boolean(),
            hasTemporaryPassword: z.boolean(),
            createdAt: dateSchema,
            lastLoginAt: dateSchema.optional(),
        })),
        total: z.number().int(),
        page: z.number().int(),
        limit: z.number().int(),
        totalPages: z.number().int(),
    }),
});
export const ResendInvitationResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        success: z.boolean(),
        message: z.string(),
    }),
});
export const TenantActionResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        tenant: z.object({
            id: uuidSchema,
            name: z.string(),
            subdomain: z.string(),
            isActive: z.boolean(),
            updatedAt: dateSchema,
        }),
    }),
});
//# sourceMappingURL=provider-tenants.schemas.js.map